import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarIcon, Ellipsis } from 'lucide-react'
import moment from 'moment'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Calendar } from '@/components/ui/calendar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DocumentData } from '@/interfaces/DocumentData'
import API from '@/lib/api'
import { formatDate } from '@/lib/formats/formatDate'

const schema = z.object({
  paidAt: z.string(),
})

type MarkDocumentPaidFormData = z.infer<typeof schema>

export function MarkDocumentPaidForm({
  document,
  linkSegment,
}: {
  document: DocumentData
  linkSegment: 'invoice' | 'expense' | 'order'
}) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'documentDetail',
  })
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const form = useForm<MarkDocumentPaidFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      paidAt: document.paidAt
        ? moment(document.paidAt).toISOString()
        : new Date().toISOString(),
    },
  })

  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState<Date | undefined>(
    moment(form.getValues('paidAt')).toDate(),
  )

  const onSubmit = (data: MarkDocumentPaidFormData) => {
    API.post(
      `/api/v2/paid-${linkSegment}s/${document.uuid}`,
      {
        paidAt: data.paidAt,
      },
    ).then(({ status }) => {
      if (status === 204) {
        toast.success(t('markPaid.success.title'))
      } else {
        toast.error(t('markPaid.error.title'))
      }

      window.location.reload()
    })
  }

  const date = moment(form.watch('paidAt'))

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex w-full items-end gap-2 sm:w-auto">
          <FormField
            control={form.control}
            name="paidAt"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1 sm:flex-none">
                {/* <FormLabel>{t('markPaid.form.paidAt.label')}</FormLabel> */}
                <FormControl>
                  <div className="relative flex gap-2">
                    <Input
                      // placeholder="June 01, 2025"
                      className="bg-background pr-10"
                      // onChange={(e) => {
                      //   const date = new Date(e.target.value)
                      //   setValue(e.target.value)
                      //   if (isValidDate(date)) {
                      //     setDate(date)
                      //     setMonth(date)
                      //   }
                      // }}
                      {...field}
                      value={
                        date.isValid() ? formatDate(field.value) : field.value
                      }
                      onChange={(e) => {
                        form.setValue('paidAt', e.target.value)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault()
                          setOpen(true)
                        }
                      }}
                    />
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id="date-picker"
                          variant="ghost"
                          className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
                        >
                          <CalendarIcon className="size-3.5" />
                          <span className="sr-only">{tg('pickDate')}</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto overflow-hidden p-0"
                        align="end"
                        alignOffset={-8}
                        sideOffset={10}
                      >
                        <Calendar
                          mode="single"
                          selected={
                            form.getValues('paidAt')
                              ? new Date(form.getValues('paidAt'))
                              : undefined
                          }
                          captionLayout="dropdown"
                          month={month}
                          onMonthChange={setMonth}
                          onSelect={(date) => {
                            form.setValue('paidAt', date?.toISOString() || '')
                            setOpen(false)
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DropdownMenu>
            <ButtonGroup>
              <Button type="submit">
                {t('markPaid.form.submit')}
              </Button>
              <DropdownMenuTrigger asChild>
                <Button className="px-2" aria-label="Další možnosti úhrady">
                  <Ellipsis className="size-4" />
                </Button>
              </DropdownMenuTrigger>
            </ButtonGroup>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  form.setValue(
                    'paidAt',
                    document.issuedAt || new Date().toISOString(),
                  )
                }}
              >
                {t('markPaid.form.whenIssued')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  form.setValue(
                    'paidAt',
                    document.deadlineAt || new Date().toISOString(),
                  )
                }}
              >
                {t('markPaid.form.onDeadline')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </form>
    </Form>
  )
}
